import Spinner from '@alexis/components/Spinner';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { generateCUID } from '@alexis/helpers/identity';
import { translateText } from '@alexis/helpers/translation';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { cloudinaryImageTransformations } from '@helpers/imageTransformations';

import { useHandleLoginEffect } from '@hooks/useHandleLoginEffect';
import { useIsAuthNew } from '@hooks/useIsAuthNew';

import { getLocaleState, getTranslationsState, getUserState } from '@redux/selectors';

import { Translations } from '@wegoTypes/translations';
import { User } from '@wegoTypes/user';

import styles from './HotelLoginBanner.module.scss';

export interface HotelLoginBannerProps {
  hotelCityName?: string;
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
}

const HotelLoginBanner: React.FC<HotelLoginBannerProps> = ({
  hotelCityName,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}): JSX.Element => {
  const apiBaseUrl: string = API_BASE_URL;
  const translations: Translations = useSelector(getTranslationsState);
  const locale: string = useSelector(getLocaleState);
  const user: User | null = useSelector(getUserState);
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const { coreConfigsLoaded, login, isAuthNew } = useIsAuthNew();

  const pageViewId = useMemo<string>(() => {
    return generateCUID();
  }, []);

  const handleLogin = useCallback<() => void>(() => {
    const eventData = {
      id: pageViewId,
      category: 'signup',
      object: 'hotel_results',
      action: 'click',
      value: '',
    };

    genzoTrackActionEvent(
      apiBaseUrl,
      wegoAnalyticsClientId!,
      wegoAnalyticsClientSessionId!,
      undefined,
      eventData,
    );

    if (isAuthNew) {
      login();
      return;
    }

    const searchParams = new URLSearchParams(search);

    searchParams.delete('modal');
    searchParams.append('modal', 'login');

    navigate({ pathname, search: searchParams.toString() }, { replace: true, state });
  }, [pageViewId, wegoAnalyticsClientId, wegoAnalyticsClientSessionId, pathname, search]);

  const { loginEffectLoading, setUserIsLoggingIn } = useHandleLoginEffect(coreConfigsLoaded, () =>
    handleLogin(),
  );

  return (
    <>
      {!user ? (
        <div className={styles.loginBanner}>
          <img
            src={cloudinaryImageTransformations(
              'https://assets.wego.com/image/upload/v1632812144/web/misc/member-rate-icon-v2.png',
              'c_fill',
              'f_auto',
              'q_auto:low',
              'fl_lossy',
              'w_96',
              'h_96',
            )}
          />

          <div className={styles.content}>
            <div
              className={styles.message}
              dangerouslySetInnerHTML={{
                __html: translateText(
                  translations.signin_banner_title_v2,
                  locale,
                  `<span>${translations.sign_in},</span>`,
                  50,
                ),
              }}
            ></div>
            <div>
              {translateText(translations.signin_banner_text_v2, locale, hotelCityName || '')}
            </div>
          </div>

          <div id='loginBannerCta' className={styles.loginButtonContainer}>
            <button onClick={() => setUserIsLoggingIn(true)} disabled={loginEffectLoading}>
              {loginEffectLoading ? <Spinner /> : translations.login}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default HotelLoginBanner;
